import React, { useState } from 'react';
import { X, Globe, Send, MessageSquare, FileText, Stethoscope } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰' },
  { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
];

const FORM_TYPES = {
  QUOTE: 'quote',
  DIAGNOSTIC: 'diagnostic',
  QUESTION: 'question'
};

const TRANSLATIONS = {
  en: {
    title: 'Contact Us in Your Language',
    selectLanguage: 'Select Your Language',
    selectType: 'What do you need?',
    requestQuote: 'Request a Quote',
    freeDiagnostic: 'Free Diagnostic',
    askQuestion: 'Ask a Question',
    name: 'Your Name',
    email: 'Your Email',
    phone: 'Your Phone',
    device: 'Device Type',
    problem: 'Describe the Problem',
    message: 'Your Message',
    submit: 'Send',
    sending: 'Sending...',
    success: 'Message sent successfully! We will contact you soon.',
    error: 'Error sending message. Please try again.'
  },
  ar: {
    title: 'اتصل بنا بلغتك',
    selectLanguage: 'اختر لغتك',
    selectType: 'ماذا تحتاج؟',
    requestQuote: 'طلب عرض أسعار',
    freeDiagnostic: 'تشخيص مجاني',
    askQuestion: 'اطرح سؤالاً',
    name: 'اسمك',
    email: 'بريدك الإلكتروني',
    phone: 'رقم هاتفك',
    device: 'نوع الجهاز',
    problem: 'وصف المشكلة',
    message: 'رسالتك',
    submit: 'إرسال',
    sending: 'جاري الإرسال...',
    success: 'تم إرسال الرسالة بنجاح! سنتواصل معك قريباً.',
    error: 'خطأ في إرسال الرسالة. يرجى المحاولة مرة أخرى.'
  },
  es: {
    title: 'Contáctenos en su Idioma',
    selectLanguage: 'Seleccione su Idioma',
    selectType: '¿Qué necesitas?',
    requestQuote: 'Solicitar Presupuesto',
    freeDiagnostic: 'Diagnóstico Gratuito',
    askQuestion: 'Hacer una Pregunta',
    name: 'Tu Nombre',
    email: 'Tu Email',
    phone: 'Tu Teléfono',
    device: 'Tipo de Dispositivo',
    problem: 'Describe el Problema',
    message: 'Tu Mensaje',
    submit: 'Enviar',
    sending: 'Enviando...',
    success: '¡Mensaje enviado con éxito! Te contactaremos pronto.',
    error: 'Error al enviar el mensaje. Por favor, inténtalo de nuevo.'
  },
  zh: {
    title: '用您的语言联系我们',
    selectLanguage: '选择您的语言',
    selectType: '您需要什么？',
    requestQuote: '请求报价',
    freeDiagnostic: '免费诊断',
    askQuestion: '提问',
    name: '您的姓名',
    email: '您的电子邮件',
    phone: '您的电话',
    device: '设备类型',
    problem: '描述问题',
    message: '您的消息',
    submit: '发送',
    sending: '发送中...',
    success: '消息发送成功！我们会尽快联系您。',
    error: '发送消息时出错。请重试。'
  },
  it: {
    title: 'Contattaci nella Tua Lingua',
    selectLanguage: 'Seleziona la Tua Lingua',
    selectType: 'Di cosa hai bisogno?',
    requestQuote: 'Richiedi un Preventivo',
    freeDiagnostic: 'Diagnostica Gratuita',
    askQuestion: 'Fai una Domanda',
    name: 'Il Tuo Nome',
    email: 'La Tua Email',
    phone: 'Il Tuo Telefono',
    device: 'Tipo di Dispositivo',
    problem: 'Descrivi il Problema',
    message: 'Il Tuo Messaggio',
    submit: 'Invia',
    sending: 'Invio...',
    success: 'Messaggio inviato con successo! Ti contatteremo presto.',
    error: 'Errore nell\'invio del messaggio. Riprova.'
  },
  de: {
    title: 'Kontaktieren Sie uns in Ihrer Sprache',
    selectLanguage: 'Wählen Sie Ihre Sprache',
    selectType: 'Was brauchen Sie?',
    requestQuote: 'Angebot Anfordern',
    freeDiagnostic: 'Kostenlose Diagnose',
    askQuestion: 'Eine Frage Stellen',
    name: 'Ihr Name',
    email: 'Ihre E-Mail',
    phone: 'Ihre Telefonnummer',
    device: 'Gerätetyp',
    problem: 'Beschreiben Sie das Problem',
    message: 'Ihre Nachricht',
    submit: 'Senden',
    sending: 'Wird gesendet...',
    success: 'Nachricht erfolgreich gesendet! Wir werden uns bald bei Ihnen melden.',
    error: 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.'
  },
  pt: {
    title: 'Entre em Contato no Seu Idioma',
    selectLanguage: 'Selecione Seu Idioma',
    selectType: 'O que você precisa?',
    requestQuote: 'Solicitar Orçamento',
    freeDiagnostic: 'Diagnóstico Gratuito',
    askQuestion: 'Fazer uma Pergunta',
    name: 'Seu Nome',
    email: 'Seu Email',
    phone: 'Seu Telefone',
    device: 'Tipo de Dispositivo',
    problem: 'Descreva o Problema',
    message: 'Sua Mensagem',
    submit: 'Enviar',
    sending: 'Enviando...',
    success: 'Mensagem enviada com sucesso! Entraremos em contato em breve.',
    error: 'Erro ao enviar mensagem. Por favor, tente novamente.'
  },
  tr: {
    title: 'Kendi Dilinizde Bize Ulaşın',
    selectLanguage: 'Dilinizi Seçin',
    selectType: 'Neye ihtiyacınız var?',
    requestQuote: 'Teklif İsteyin',
    freeDiagnostic: 'Ücretsiz Teşhis',
    askQuestion: 'Soru Sorun',
    name: 'Adınız',
    email: 'E-postanız',
    phone: 'Telefonunuz',
    device: 'Cihaz Türü',
    problem: 'Sorunu Açıklayın',
    message: 'Mesajınız',
    submit: 'Gönder',
    sending: 'Gönderiliyor...',
    success: 'Mesaj başarıyla gönderildi! Yakında sizinle iletişime geçeceğiz.',
    error: 'Mesaj gönderilirken hata oluştu. Lütfen tekrar deneyin.'
  },
  ru: {
    title: 'Свяжитесь с нами на вашем языке',
    selectLanguage: 'Выберите ваш язык',
    selectType: 'Что вам нужно?',
    requestQuote: 'Запросить Расценки',
    freeDiagnostic: 'Бесплатная Диагностика',
    askQuestion: 'Задать Вопрос',
    name: 'Ваше Имя',
    email: 'Ваш Email',
    phone: 'Ваш Телефон',
    device: 'Тип Устройства',
    problem: 'Опишите Проблему',
    message: 'Ваше Сообщение',
    submit: 'Отправить',
    sending: 'Отправка...',
    success: 'Сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.',
    error: 'Ошибка отправки сообщения. Пожалуйста, попробуйте снова.'
  },
  ja: {
    title: 'あなたの言語でお問い合わせ',
    selectLanguage: '言語を選択',
    selectType: '何が必要ですか？',
    requestQuote: '見積もりをリクエスト',
    freeDiagnostic: '無料診断',
    askQuestion: '質問する',
    name: 'お名前',
    email: 'メールアドレス',
    phone: '電話番号',
    device: 'デバイスの種類',
    problem: '問題を説明',
    message: 'メッセージ',
    submit: '送信',
    sending: '送信中...',
    success: 'メッセージが正常に送信されました！まもなくご連絡いたします。',
    error: 'メッセージの送信エラー。もう一度お試しください。'
  },
  ko: {
    title: '귀하의 언어로 문의하기',
    selectLanguage: '언어 선택',
    selectType: '무엇이 필요하십니까?',
    requestQuote: '견적 요청',
    freeDiagnostic: '무료 진단',
    askQuestion: '질문하기',
    name: '이름',
    email: '이메일',
    phone: '전화번호',
    device: '기기 유형',
    problem: '문제 설명',
    message: '메시지',
    submit: '보내기',
    sending: '전송 중...',
    success: '메시지가 성공적으로 전송되었습니다! 곧 연락드리겠습니다.',
    error: '메시지 전송 오류. 다시 시도해주세요.'
  },
  hi: {
    title: 'अपनी भाषा में हमसे संपर्क करें',
    selectLanguage: 'अपनी भाषा चुनें',
    selectType: 'आपको क्या चाहिए?',
    requestQuote: 'कोटेशन का अनुरोध करें',
    freeDiagnostic: 'मुफ्त निदान',
    askQuestion: 'प्रश्न पूछें',
    name: 'आपका नाम',
    email: 'आपका ईमेल',
    phone: 'आपका फोन',
    device: 'डिवाइस का प्रकार',
    problem: 'समस्या का वर्णन करें',
    message: 'आपका संदेश',
    submit: 'भेजें',
    sending: 'भेजा जा रहा है...',
    success: 'संदेश सफलतापूर्वक भेजा गया! हम जल्द ही आपसे संपर्क करेंगे।',
    error: 'संदेश भेजने में त्रुटि। कृपया पुनः प्रयास करें।'
  },
  ur: {
    title: 'اپنی زبان میں ہم سے رابطہ کریں',
    selectLanguage: 'اپنی زبان منتخب کریں',
    selectType: 'آپ کو کیا چاہیے؟',
    requestQuote: 'قیمت کی درخواست کریں',
    freeDiagnostic: 'مفت تشخیص',
    askQuestion: 'سوال پوچھیں',
    name: 'آپ کا نام',
    email: 'آپ کا ای میل',
    phone: 'آپ کا فون',
    device: 'ڈیوائس کی قسم',
    problem: 'مسئلہ بیان کریں',
    message: 'آپ کا پیغام',
    submit: 'بھیجیں',
    sending: 'بھیجا جا رہا ہے...',
    success: 'پیغام کامیابی سے بھیج دیا گیا! ہم جلد ہی آپ سے رابطہ کریں گے۔',
    error: 'پیغام بھیجنے میں خرابی۔ براہ کرم دوبارہ کوشش کریں۔'
  },
  fa: {
    title: 'با ما به زبان خودتان تماس بگیرید',
    selectLanguage: 'زبان خود را انتخاب کنید',
    selectType: 'به چه چیزی نیاز دارید؟',
    requestQuote: 'درخواست قیمت',
    freeDiagnostic: 'تشخیص رایگان',
    askQuestion: 'سوال بپرسید',
    name: 'نام شما',
    email: 'ایمیل شما',
    phone: 'تلفن شما',
    device: 'نوع دستگاه',
    problem: 'مشکل را شرح دهید',
    message: 'پیام شما',
    submit: 'ارسال',
    sending: 'در حال ارسال...',
    success: 'پیام با موفقیت ارسال شد! به زودی با شما تماس خواهیم گرفت.',
    error: 'خطا در ارسال پیام. لطفاً دوباره امتحان کنید.'
  },
  pl: {
    title: 'Skontaktuj się z nami w swoim języku',
    selectLanguage: 'Wybierz swój język',
    selectType: 'Czego potrzebujesz?',
    requestQuote: 'Poproś o Wycenę',
    freeDiagnostic: 'Darmowa Diagnostyka',
    askQuestion: 'Zadaj Pytanie',
    name: 'Twoje Imię',
    email: 'Twój Email',
    phone: 'Twój Telefon',
    device: 'Typ Urządzenia',
    problem: 'Opisz Problem',
    message: 'Twoja Wiadomość',
    submit: 'Wyślij',
    sending: 'Wysyłanie...',
    success: 'Wiadomość wysłana pomyślnie! Wkrótce się z Tobą skontaktujemy.',
    error: 'Błąd wysyłania wiadomości. Spróbuj ponownie.'
  },
  nl: {
    title: 'Neem contact met ons op in uw taal',
    selectLanguage: 'Selecteer uw taal',
    selectType: 'Wat heeft u nodig?',
    requestQuote: 'Offerte Aanvragen',
    freeDiagnostic: 'Gratis Diagnose',
    askQuestion: 'Stel een Vraag',
    name: 'Uw Naam',
    email: 'Uw Email',
    phone: 'Uw Telefoon',
    device: 'Type Apparaat',
    problem: 'Beschrijf het Probleem',
    message: 'Uw Bericht',
    submit: 'Verzenden',
    sending: 'Verzenden...',
    success: 'Bericht succesvol verzonden! We nemen binnenkort contact met u op.',
    error: 'Fout bij het verzenden van bericht. Probeer het opnieuw.'
  },
  vi: {
    title: 'Liên hệ với chúng tôi bằng ngôn ngữ của bạn',
    selectLanguage: 'Chọn ngôn ngữ của bạn',
    selectType: 'Bạn cần gì?',
    requestQuote: 'Yêu cầu Báo giá',
    freeDiagnostic: 'Chẩn đoán Miễn phí',
    askQuestion: 'Đặt Câu hỏi',
    name: 'Tên của bạn',
    email: 'Email của bạn',
    phone: 'Điện thoại của bạn',
    device: 'Loại Thiết bị',
    problem: 'Mô tả Vấn đề',
    message: 'Tin nhắn của bạn',
    submit: 'Gửi',
    sending: 'Đang gửi...',
    success: 'Tin nhắn đã được gửi thành công! Chúng tôi sẽ liên hệ với bạn sớm.',
    error: 'Lỗi khi gửi tin nhắn. Vui lòng thử lại.'
  },
  th: {
    title: 'ติดต่อเราในภาษาของคุณ',
    selectLanguage: 'เลือกภาษาของคุณ',
    selectType: 'คุณต้องการอะไร?',
    requestQuote: 'ขอใบเสนอราคา',
    freeDiagnostic: 'การวินิจฉัยฟรี',
    askQuestion: 'ถามคำถาม',
    name: 'ชื่อของคุณ',
    email: 'อีเมลของคุณ',
    phone: 'โทรศัพท์ของคุณ',
    device: 'ประเภทอุปกรณ์',
    problem: 'อธิบายปัญหา',
    message: 'ข้อความของคุณ',
    submit: 'ส่ง',
    sending: 'กำลังส่ง...',
    success: 'ส่งข้อความสำเร็จ! เราจะติดต่อคุณเร็วๆ นี้',
    error: 'เกิดข้อผิดพลาดในการส่งข้อความ กรุณาลองอีกครั้ง'
  },
  el: {
    title: 'Επικοινωνήστε μαζί μας στη γλώσσα σας',
    selectLanguage: 'Επιλέξτε τη γλώσσα σας',
    selectType: 'Τι χρειάζεστε;',
    requestQuote: 'Ζητήστε Προσφορά',
    freeDiagnostic: 'Δωρεάν Διάγνωση',
    askQuestion: 'Κάντε μια Ερώτηση',
    name: 'Το Όνομά σας',
    email: 'Το Email σας',
    phone: 'Το Τηλέφωνό σας',
    device: 'Τύπος Συσκευής',
    problem: 'Περιγράψτε το Πρόβλημα',
    message: 'Το Μήνυμά σας',
    submit: 'Αποστολή',
    sending: 'Αποστολή...',
    success: 'Το μήνυμα στάλθηκε με επιτυχία! Θα επικοινωνήσουμε μαζί σας σύντομα.',
    error: 'Σφάλμα αποστολής μηνύματος. Παρακαλώ δοκιμάστε ξανά.'
  },
  ro: {
    title: 'Contactați-ne în limba dvs',
    selectLanguage: 'Selectați limba dvs',
    selectType: 'De ce aveți nevoie?',
    requestQuote: 'Solicitați o Ofertă',
    freeDiagnostic: 'Diagnostic Gratuit',
    askQuestion: 'Puneți o Întrebare',
    name: 'Numele dvs',
    email: 'Email-ul dvs',
    phone: 'Telefonul dvs',
    device: 'Tip Dispozitiv',
    problem: 'Descrieți Problema',
    message: 'Mesajul dvs',
    submit: 'Trimite',
    sending: 'Se trimite...',
    success: 'Mesaj trimis cu succes! Vă vom contacta în curând.',
    error: 'Eroare la trimiterea mesajului. Vă rugăm să încercați din nou.'
  }
};

export const MultilingualForm = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('en');
  const [showOtherLangInput, setShowOtherLangInput] = useState(false);
  const [otherLanguage, setOtherLanguage] = useState('');
  const [formType, setFormType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    device: '',
    problem: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const t = TRANSLATIONS[selectedLang] || TRANSLATIONS.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const backendUrl = window.location.origin;
      const endpoint = formType === FORM_TYPES.DIAGNOSTIC ? '/api/diagnostic' : '/api/quotes';
      
      // Déterminer la langue sélectionnée
      const finalLanguage = showOtherLangInput && otherLanguage ? otherLanguage : selectedLang;
      
      // Préparer le payload selon le format backend
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        deviceType: formData.device || 'Non spécifié',
        brand: '',
        model: '',
        problem: `[Langue: ${finalLanguage}] ${formData.problem || formData.message || 'Demande générale'}`,
        message: formData.message || ''
      };

      // Ajouter address pour diagnostic
      if (formType === FORM_TYPES.DIAGNOSTIC) {
        payload.address = '';
      }

      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setStatus({ type: 'success', message: t.success });
        setFormData({ name: '', email: '', phone: '', device: '', problem: '', message: '' });
        setTimeout(() => {
          setIsOpen(false);
          setFormType(null);
          setStatus({ type: '', message: '' });
        }, 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Erreur backend:', errorData);
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Error:', error);
      setStatus({ type: 'error', message: t.error });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Bouton flottant multilingue - VISIBLE HERO */}
      <div className="flex justify-center mt-6 mb-4">
        <button
          onClick={() => setIsOpen(true)}
          className="multilingual-trigger bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 flex items-center space-x-3 border-4 border-white"
          title="Speak Your Language"
        >
          <Globe size={28} className="animate-spin-slow" />
          <span className="font-bold text-lg">🌐 Speak Your Language</span>
        </button>
      </div>

      {/* Modal multilingue */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 md:p-4 overflow-y-auto"
          onClick={() => setIsOpen(false)}
        >
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full my-auto max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white p-6 sticky top-0 z-10 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Globe size={32} />
                  <h2 className="text-2xl font-bold">{t.title}</h2>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-red-600 bg-red-500 rounded-full transition-colors"
                >
                  <X size={24} strokeWidth={3} />
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6 space-y-6">
              {/* Sélection de langue */}
              {!formType && (
                <>
                  <div>
                    <label className="block text-gray-700 font-semibold mb-3 text-lg">
                      {t.selectLanguage}
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {LANGUAGES.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setSelectedLang(lang.code);
                            setShowOtherLangInput(false);
                            setOtherLanguage('');
                          }}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            selectedLang === lang.code && !showOtherLangInput
                              ? 'border-green-600 bg-green-50 shadow-lg scale-105'
                              : 'border-gray-300 hover:border-green-400'
                          }`}
                        >
                          <div className="text-3xl mb-1">{lang.flag}</div>
                          <div className="text-sm font-semibold">{lang.name}</div>
                        </button>
                      ))}
                      
                      {/* Bouton "Autre langue" */}
                      <button
                        onClick={() => {
                          setShowOtherLangInput(true);
                          setSelectedLang('other');
                        }}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          showOtherLangInput
                            ? 'border-green-600 bg-green-50 shadow-lg scale-105'
                            : 'border-gray-300 hover:border-green-400'
                        }`}
                      >
                        <div className="text-3xl mb-1">🗣️</div>
                        <div className="text-sm font-semibold">Autre langue</div>
                      </button>
                    </div>
                    
                    {/* Input pour langue personnalisée */}
                    {showOtherLangInput && (
                      <div className="mt-4">
                        <input
                          type="text"
                          placeholder="Entrez votre langue (ex: Swahili, Tagalog...)"
                          value={otherLanguage}
                          onChange={(e) => setOtherLanguage(e.target.value)}
                          className="w-full px-4 py-3 border-2 border-green-600 rounded-xl focus:outline-none"
                        />
                      </div>
                    )}
                  </div>

                  {/* Sélection du type de demande */}
                  <div>
                    <label className="block text-gray-700 font-semibold mb-3 text-lg">
                      {t.selectType}
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        onClick={() => setFormType(FORM_TYPES.QUOTE)}
                        className="p-6 bg-blue-50 border-2 border-blue-300 rounded-xl hover:bg-blue-100 transition-all"
                      >
                        <FileText className="mx-auto mb-2 text-blue-600" size={32} />
                        <div className="font-bold">{t.requestQuote}</div>
                      </button>
                      <button
                        onClick={() => setFormType(FORM_TYPES.DIAGNOSTIC)}
                        className="p-6 bg-green-50 border-2 border-green-300 rounded-xl hover:bg-green-100 transition-all"
                      >
                        <Stethoscope className="mx-auto mb-2 text-green-600" size={32} />
                        <div className="font-bold">{t.freeDiagnostic}</div>
                      </button>
                      <button
                        onClick={() => setFormType(FORM_TYPES.QUESTION)}
                        className="p-6 bg-purple-50 border-2 border-purple-300 rounded-xl hover:bg-purple-100 transition-all"
                      >
                        <MessageSquare className="mx-auto mb-2 text-purple-600" size={32} />
                        <div className="font-bold">{t.askQuestion}</div>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Formulaire */}
              {formType && (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <button
                    type="button"
                    onClick={() => setFormType(null)}
                    className="text-green-600 hover:underline flex items-center space-x-2 mb-4"
                  >
                    <span>← {t.selectLanguage}</span>
                  </button>

                  <input
                    type="text"
                    placeholder={t.name}
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-600 focus:outline-none"
                  />

                  <input
                    type="email"
                    placeholder={t.email}
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-600 focus:outline-none"
                  />

                  <input
                    type="tel"
                    placeholder={t.phone}
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-600 focus:outline-none"
                  />

                  {formType !== FORM_TYPES.QUESTION && (
                    <input
                      type="text"
                      placeholder={t.device}
                      value={formData.device}
                      onChange={(e) => setFormData({...formData, device: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-600 focus:outline-none"
                    />
                  )}

                  <textarea
                    placeholder={formType === FORM_TYPES.QUESTION ? t.message : t.problem}
                    value={formType === FORM_TYPES.QUESTION ? formData.message : formData.problem}
                    onChange={(e) => setFormData({
                      ...formData, 
                      [formType === FORM_TYPES.QUESTION ? 'message' : 'problem']: e.target.value
                    })}
                    required
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-green-600 focus:outline-none resize-none"
                  />

                  {status.message && (
                    <div className={`p-4 rounded-xl ${
                      status.type === 'success' 
                        ? 'bg-green-100 text-green-800 border-2 border-green-300' 
                        : 'bg-red-100 text-red-800 border-2 border-red-300'
                    }`}>
                      {status.message}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-700 text-white px-6 py-4 rounded-xl font-bold text-lg hover:from-green-700 hover:to-emerald-800 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span>{t.sending}</span>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>{t.submit}</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
